// Doc-Ref: docs/features/collaborations-plan.md
import { AxiosConfigExplore } from "Explore/Config/AxiosConfig";

/**
 * Create a new background report generation job
 */
export const createReportJob = ({
  userId,
  entity,
  entities = [],
  entityType,
  filters,
  sections,
  metrics,
  title,
  format = 'pdf',
  module = 'collaboration',
  visualOptions = null,
  sortBy,
  sortOrder,
}) => {
  const axios = AxiosConfigExplore();
  const isAnalysis = module === "analysis";

  if (isAnalysis) {
    const payload = {
      title,
      module: "analysis",
      entity_type: entityType || entity?.type || entities?.[0]?.type || "institution",
      entity_ids: entities && entities.length > 0 ? entities.map((e) => e.id) : [],
      export_format: format,
      filters: {
        year_range: filters?.yearRange || "all",
        subject_area: filters?.subjectArea && filters.subjectArea.length > 0 ? filters.subjectArea : undefined,
        keywords_operator: filters?.keywordsOperator || "or",
        state: filters?.state || [],
        sort_by: sortBy || "total_pubs",
        sort_order: sortOrder || "desc",
      },
      ...(format === "xlsx"
        ? {
            metrics: metrics && metrics.length > 0 ? metrics : undefined,
          }
        : {
            sections,
            ...(visualOptions && { visual_options: visualOptions }),
          }),
    };

    return axios
      .post("/export-center/jobs", payload)
      .then((res) => res.data);
  }

  const payload = {
    user_id: userId || "current_user",
    title,
    entity_name: entity?.name,
    entity_type: entity?.type || entities?.[0]?.type || "institution",
    entity_ids: entity?.id ? [entity.id] : (entities && entities.length > 0 ? [entities[0].id] : []),
    export_format: format,
    module: "collaboration",
    filters: {
      publication_year_start: filters?.yearRange?.[0],
      publication_year_end: filters?.yearRange?.[1],
      yearRange: filters?.yearRange,
      year_range: filters?.yearRange,
      geography: filters?.geography,
      metric: filters?.metric,
      collaborator_type: filters?.collaboratorType,
      subject_area: filters?.subjectArea && filters.subjectArea.length > 0 ? filters.subjectArea : undefined,
      state: filters?.state,
      keywords_operator: filters?.keywordsOperator,
    },
    sections,
  };

  return axios
    .post("/export-center/jobs", payload)
    .then((res) => res.data);
};

/**
 * Poll the status of a report job
 */
export const getReportJobStatus = (jobId) => {
  const axios = AxiosConfigExplore();
  return axios
    .get(`/export-center/jobs/${jobId}`)
    .then((res) => res.data);
};

/**
 * Get the history of generated reports for a user or module
 */
export const getReportJobs = (params = {}) => {
  const axios = AxiosConfigExplore();
  const queryParams = typeof params === "string" ? { user_id: params } : params;
  return axios
    .get(`/export-center/jobs`, { params: queryParams })
    .then((res) => res.data);
};

/**
 * Delete a report job
 */
export const deleteReportJob = (jobId) => {
  const axios = AxiosConfigExplore();
  return axios
    .delete(`/export-center/jobs/${jobId}`)
    .then((res) => res.data);
};

/**
 * Mark multiple report jobs as seen
 */
export const markJobsSeen = (jobIds) => {
  const axios = AxiosConfigExplore();
  return axios
    .patch(`/export-center/jobs/seen-bulk`, { job_ids: jobIds })
    .then((res) => res.data);
};

/**
 * @typedef {'analysis' | 'collaboration'} ExportModule
 * @typedef {'pdf' | 'xlsx'} ExportFormat
 * @typedef {'pending' | 'processing' | 'ready' | 'failed'} ExportJobStatus
 * @typedef {'institution' | 'researcher' | 'unit'} EntityType
 *
 * @typedef {Object} ExportCenterJob
 * @property {string} id
 * @property {string} user_id
 * @property {string} title
 * @property {ExportModule} module
 * @property {string|null} [entity_id]
 * @property {string[]|null} [entity_ids]
 * @property {EntityType} entity_type
 * @property {string|null} [entity_name]
 * @property {ExportFormat} export_format
 * @property {Record<string, any>} filters
 * @property {string[]} sections
 * @property {string[]|null} [metrics]
 * @property {Record<string, any>|null} [visual_options]
 * @property {ExportJobStatus} status
 * @property {boolean} is_seen
 * @property {string|null} [error_message]
 * @property {string} created_at
 * @property {string|null} [completed_at]
 * @property {string|null} [expires_at]
 */

/**
 * Download a completed export report file (HTTP 307 redirect to S3 Presigned URL)
 * @param {string} jobId UUID of the export job
 * @param {string} defaultFilename Fallback filename if Content-Disposition header is absent
 */
export const downloadExportReport = async (jobId, defaultFilename = 'report.pdf') => {
  const axios = AxiosConfigExplore();
  try {
    const response = await axios.get(`/export-center/jobs/${jobId}/download`, {
      responseType: 'blob',
    });

    // 1. Extract filename from Content-Disposition header if available
    let filename = defaultFilename;
    const disposition = response.headers?.['content-disposition'];
    if (disposition) {
      const filenameMatch = disposition.match(/filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i);
      if (filenameMatch) {
        filename = decodeURIComponent(filenameMatch[1] || filenameMatch[2]);
      }
    }

    // 2. Create Blob URL and trigger browser download
    const blob = new Blob([response.data], {
      type: response.headers?.['content-type'] || 'application/octet-stream',
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();

    // 3. Cleanup DOM and object URL
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    // If response was blob, error response data might also be a blob containing JSON
    let errorMessage = null;
    if (error.response?.data instanceof Blob) {
      try {
        const errorText = await error.response.data.text();
        const parsed = JSON.parse(errorText);
        errorMessage = parsed.detail || parsed.message;
      } catch (e) {
        // Ignore parse error
      }
    }

    if (!errorMessage) {
      if (error.response?.status === 400) {
        errorMessage = 'Báo cáo chưa hoàn thành hoặc đã thất bại. Vui lòng thử lại sau.';
      } else if (error.response?.status === 404) {
        errorMessage = 'Báo cáo không tồn tại hoặc bạn không có quyền truy cập.';
      } else if (error.response?.status === 401) {
        errorMessage = 'Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.';
      } else {
        errorMessage = error.message || 'Không thể tải file báo cáo. Vui lòng kiểm tra lại kết nối mạng.';
      }
    }

    const enhancedError = new Error(errorMessage);
    enhancedError.status = error.response?.status;
    enhancedError.originalError = error;
    throw enhancedError;
  }
};
